import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { socialButtonClassName } from './socialButton';
import config from 'config';
import { acquisitionQueryParam } from 'lib/acquisition';

interface GoogleSignInButtonProps {
  /** Org onboarding carried from an invite / team-join link. */
  inviteToken?: string;
  teamJoinToken?: string;
  orgInviteCode?: string;
  /** Referral code from a ?ref= link (self-serve signups only). */
  referralCode?: string;
  /** Internal path to land on after a successful sign-in. */
  redirectTo?: string;
  accountType?: 'STAFF' | 'CANDIDATE';
  /** 'login' = the Login page: the API refuses to create an account and bounces back with an error. */
  mode?: 'login' | 'signup';
  /** Optional custom button content or label */
  children?: React.ReactNode;
  /** Extra classes for the outer button surface. */
  className?: string;
}

/** Google's four-colour "G" mark. */
function GoogleGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill="#4285F4"
        d="M23.52 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.87z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.76-2.11-6.7-4.94H1.29v3.09A11.997 11.997 0 0 0 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.3 14.31a7.2 7.2 0 0 1-.38-2.31c0-.8.14-1.58.38-2.31V6.6H1.29A11.997 11.997 0 0 0 0 12c0 1.94.46 3.77 1.29 5.4l4.01-3.09z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.76 0 3.34.61 4.58 1.79l3.44-3.44C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.69 1.29 6.6l4.01 3.09C6.24 6.86 8.88 4.75 12 4.75z"
      />
    </svg>
  );
}

/** A top-level redirect, not the popup/GIS flow, which silently fails in in-app browsers (Telegram, Instagram). */
export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  inviteToken,
  teamJoinToken,
  orgInviteCode,
  referralCode,
  redirectTo = '/app',
  accountType,
  mode,
  children,
  className,
}) => {
  const { t } = useTranslation();
  if (!config.google.client_id) return null;

  const startGoogleSignIn = () => {
    const params = new URLSearchParams();
    params.set('redirect', redirectTo);
    const effectiveOrgInvite = orgInviteCode || sessionStorage.getItem('pending_org_invite_code') || undefined;
    if (accountType) params.set('type', accountType);
    if (mode) params.set('mode', mode);
    if (inviteToken) params.set('invite', inviteToken);
    if (teamJoinToken) params.set('team', teamJoinToken);
    if (effectiveOrgInvite) params.set('orgInvite', effectiveOrgInvite);
    if (referralCode) params.set('ref', referralCode);
    const acq = acquisitionQueryParam();
    if (acq) params.set('acq', acq);
    // Full-page navigation (not window.open) so it works inside in-app WebViews.
    window.location.assign(
      `${config.api.base_url}/api/auth/google?${params.toString()}`,
    );
  };

  return (
    <button
      type="button"
      onClick={startGoogleSignIn}
      className={cn(socialButtonClassName, className)}
    >
      <GoogleGlyph className="h-[17px] w-[17px] shrink-0" />
      <span>{children ?? t('auth.continue_with_google')}</span>
    </button>
  );
};

export default GoogleSignInButton;
